import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { tableStore } from '../stores/table-store';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';
import type { ProStudioEvents } from '../xstate/prostudio.xstate.types';
import { subscribeToNodesChanged } from '../stores/utils/node-sync-channel';
import {
  CROSS_TAB_RELOAD_MIN_INTERVAL_MS,
  CROSS_TAB_RELOAD_RETRY_BACKOFF_MS,
  pendingDirtSurvives,
  reloadRetryDelayMs,
  retryDelayWithBackoff,
  shouldReloadNodes,
  shouldRetryWhileVersionMismatched,
} from '../stores/utils/cross-tab-sync';
import { isTablePage } from './coworker-subscription-gate';
import { isAnyCellEditOpen } from '../stores/utils/active-cell-edit';

/**
 * Self-heal timeout for the in-flight flag. The `reloadNodes` XState event has
 * a transition ONLY on the machine's `idle` state (prostudio.xstate.ts). XState
 * v4 SILENTLY DROPS an event with no transition in the current state, so a
 * broadcast that arrives while the machine is in any non-idle flow reachable on
 * a table page (CSV upload, refetch/reload-current-bot, publish) never runs the
 * invoke — and thus never runs its `cb`. Without a watchdog `isReloadInFlight`
 * would wedge `true` forever and block every future cross-tab reload for this
 * mount. This timeout is comfortably longer than a real network-only
 * getProStudioVersionData + processVersionData + validateAndSync at a large
 * table (~1s in practice) so it never fires on a legitimately in-flight reload;
 * it only fires when the event was dropped (or a reload genuinely hung).
 */
const RELOAD_WATCHDOG_MS = 15_000;

/**
 * PSTUDIO-3610: live cross-(browser-)tab node sync. A sibling tab broadcasts
 * after every successful node persist (node-sync-channel); this tab marks
 * itself dirty and reloads through the existing XState `reloadNodes` invoke
 * (network-only fetch → processVersionData → validateAndSync) — immediately
 * when visible, otherwise on the next visibilitychange/focus. Guards (pure,
 * unit-tested in cross-tab-sync.ts): table pages only, never while loading or
 * editing a cell, never over pending local saves, one reload in flight, ≥3s
 * between reloads. When a guard that self-resolves without an event we listen
 * for blocks a reload (the 3s throttle window, or a transient loading/edit/
 * pending-save), the tab self-schedules a retry so the change still lands —
 * see reloadRetryDelayMs (PSTUDIO-3625).
 */
export function useCrossTabNodeSync(): void {
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    // PSTUDIO-4189: the dirt carries the identity it was raised FOR, not a bare
    // boolean. Hoisting this hook out of the `isLoading` branch (PSTUDIO-4168)
    // kept its flag alive across a publish — and across a full solution switch
    // too, since BuilderPage is keyed by route label and serves any two
    // solutions from one instance. A blocked change from the solution being
    // left would otherwise fire a reload against the one being entered. `null`
    // means nothing pending. See pendingDirtSurvives.
    let dirtyForSolutionVersionId: string | null = null;
    let isReloadInFlight = false;
    let lastReloadAt: number | null = null;
    let watchdogId: ReturnType<typeof setTimeout> | null = null;
    // PSTUDIO-3625: a self-scheduled retry for a reload dropped by a block that
    // clears WITHOUT any event we listen for (the 3s throttle window; transient
    // loading / cell-edit / pending-saves). See reloadRetryDelayMs.
    let retryId: ReturnType<typeof setTimeout> | null = null;
    // PSTUDIO-4168: consecutive self-scheduled retries since the last dispatch
    // or fresh broadcast. The retry exists for blocks that clear in seconds; a
    // block that never clears (the machine parked outside `idle`, a store and
    // route that stop agreeing) would otherwise poll at 1 Hz for the life of
    // the mount — and this hook now outlives the loading flips that used to
    // unmount it, so "the life of the mount" is the whole session. This count
    // DECAYS the delay rather than ending the retry (retryDelayWithBackoff):
    // abandoning it would strand a change behind any block that outlives the
    // budget, and closing a cell edit — the common one — fires no event this
    // hook listens for.
    let consecutiveRetries = 0;

    const clearRetry = () => {
      if (retryId !== null) {
        clearTimeout(retryId);
        retryId = null;
      }
    };

    /** Self-schedule another attempt, backing off as deferrals accumulate. */
    const scheduleRetry = (delayMs: number) => {
      const delay = retryDelayWithBackoff(delayMs, consecutiveRetries);
      consecutiveRetries++;
      retryId = setTimeout(maybeReload, delay);
    };

    // Ends an in-flight reload from EITHER the invoke's cb (normal path) or the
    // watchdog (event was dropped / reload hung). Clears the watchdog timer,
    // frees the in-flight flag, and — if a broadcast landed while the reload
    // was in flight (dirt still pending) — retries via maybeReload. maybeReload
    // is fully guarded (shouldReloadNodes incl. the 3s throttle via
    // lastReloadAt); if it still can't run it self-schedules a retry (see
    // reloadRetryDelayMs / retryId), so a change dropped inside the throttle
    // window is drained when the window elapses — not only on the next
    // unrelated event. There is no synchronous re-fire loop (a dropped dispatch
    // fires nothing synchronously — a retry only ever comes back via the async
    // cb, the watchdog, or the retry timer).
    const finishReload = () => {
      if (watchdogId !== null) {
        clearTimeout(watchdogId);
        watchdogId = null;
      }
      isReloadInFlight = false;
      if (dirtyForSolutionVersionId !== null) maybeReload();
    };

    const maybeReload = () => {
      // Always drop the previous retry first so no path stacks timers: this
      // runs on every entry (broadcast, focus/visibility, finishReload, or a
      // retry firing), and each re-decides from scratch below.
      clearRetry();

      // PSTUDIO-4189: retire dirt the store has moved away from, rather than
      // letting it retarget the version now on screen. Falls through with
      // nothing pending, so every guard below reads false and no retry is
      // scheduled.
      if (
        !pendingDirtSurvives(
          dirtyForSolutionVersionId,
          tableStore.solutionVersionId
        )
      ) {
        dirtyForSolutionVersionId = null;
      }

      const guards = {
        isDirty: dirtyForSolutionVersionId !== null,
        isReloadInFlight,
        lastReloadAt,
        now: Date.now(),
        minIntervalMs: CROSS_TAB_RELOAD_MIN_INTERVAL_MS,
        isVisible: typeof document === 'undefined' ? false : !document.hidden,
        isTablePage: isTablePage(pathRef.current),
        currentState: tableStore.currentState,
        // PSTUDIO-1433: an open Expanded Cell View edit blocks the reload too.
        // The panel clears `isCellBeingEdited` when a commit is suppressed (an
        // in-panel click), which left the ONLY guard between a cross-tab reload
        // and an unsaved draft switched off — the reload then wiped the draft.
        isCellBeingEdited: isAnyCellEditOpen(tableStore),
        hasPendingSaves: tableStore.hasPendingNodeSaves,
      };
      // PSTUDIO-4168: the store and the route can name different solutions for
      // a beat — the effect in BuilderPage that moves `tableStore` runs after
      // the XState context has already changed. Until this ticket the hook was
      // unmounted for exactly that window (it lived inside the `isLoading`
      // branch, and `isLoading` covers the mismatch), so nothing had to say
      // this out loud. Now that it stays mounted to keep its dirty flag, the
      // window is reachable: a broadcast for the solution being LEFT still
      // matches `tableStore.solutionVersionId`, and the reload it triggers ends
      // in `validateAndSync` — a compiler upload and a server-side compile, and
      // any error toast they raise — for a solution the user has walked away
      // from. Wait it out instead; the retry converges once the two agree.
      //
      // qa1: read the route's identity from `solutionVersionPsId`, the same field
      // Builder and use-table-store-sync push into the store. Rebuilding it with
      // `buildSolutionVersionId` yields the legacy `<customer>-<bot>-<draft>`
      // string, which never equals a v2 uuid, so every reload waited forever.
      const routeSolutionVersionId =
        proStudioService.getSnapshot().context.solutionVersionPsId ?? '';
      if (
        !routeSolutionVersionId ||
        routeSolutionVersionId !== tableStore.solutionVersionId
      ) {
        if (shouldRetryWhileVersionMismatched(guards)) {
          scheduleRetry(CROSS_TAB_RELOAD_RETRY_BACKOFF_MS);
        }
        return;
      }
      if (!shouldReloadNodes(guards)) {
        // PSTUDIO-3625: reschedule ourselves when the block will clear with no
        // event we listen for (throttle window / transient loading-edit-save).
        // Without this, a change landing inside the 3s throttle window — e.g. a
        // rapid group collapse/expand series — is never re-attempted until an
        // unrelated broadcast/focus/visibilitychange, so the sibling tab "does
        // not always" reflect it. The dirt stays pending, so the retry converges.
        const delay = reloadRetryDelayMs(guards);
        if (delay !== null) scheduleRetry(delay);
        return;
      }
      // reloadNodes ignores payload.solutionVersionId (the invoke reloads
      // tableStore.solutionVersionId directly — see reload-nodes.ts), but the
      // event type requires it, so pass the current store value. Dispatched via
      // sendProStudioEvent (reloadNodes is a ProStudioEvents event, as in
      // use-upload-csv.ts), not sendGlobalAppEvent.
      const event: ProStudioEvents = {
        type: 'reloadNodes',
        payload: {
          solutionVersionId: tableStore.solutionVersionId,
          // Normal completion path: the invoke's finally() runs this. Routes
          // through finishReload so a change that arrived mid-reload (still dirty)
          // is drained instead of waiting for the next unrelated event.
          cb: finishReload,
          // PSTUDIO-3610: this is a background sync, not a CSV upload — suppress
          // the shared reload flow's "CSV successfully imported" toast.
          silent: true,
          // PSTUDIO-3671: the sibling tab's solution was deleted -> eject.
          onNotFound: () => sendProStudioEvent({ type: 'solutionNotFound' }),
        },
      };

      // PSTUDIO-4168: ask the machine BEFORE dispatching. `reloadNodes` has a
      // transition only on `idle`, and XState v4 drops an event with no
      // transition in the current state without a word — so the old order
      // (clear the dirt, arm the watchdog, dispatch into the void) turned a
      // sibling tab's change into a 15s stall and then silence: the watchdog
      // frees the in-flight flag, but by then the dirt is gone and there is
      // nothing left to retry. That is the tab going stale across a publish,
      // which is where two tabs start allocating the same node number from two
      // different local snapshots.
      //
      // Keeping the dirt and re-checking on the standard backoff costs one
      // predicate and makes the signal deferred rather than lost. The watchdog
      // stays for the other half of the problem it was written for — a reload
      // that is accepted and then hangs.
      if (!proStudioService.getSnapshot().can(event)) {
        scheduleRetry(CROSS_TAB_RELOAD_RETRY_BACKOFF_MS);
        return;
      }

      // PSTUDIO-4168: a real attempt is leaving — the retry budget is for
      // consecutive DEFERRALS, so it starts over here.
      consecutiveRetries = 0;
      dirtyForSolutionVersionId = null;
      isReloadInFlight = true;
      lastReloadAt = Date.now();
      // Arm the self-heal watchdog BEFORE dispatching: if the event is dropped
      // (machine not in `idle`) the cb never fires, so this is the only thing
      // that frees the flag. finishReload clears this timer on the normal path.
      if (watchdogId !== null) clearTimeout(watchdogId);
      watchdogId = setTimeout(finishReload, RELOAD_WATCHDOG_MS);
      sendProStudioEvent(event);
    };

    const unsubscribe = subscribeToNodesChanged((msg) => {
      if (msg.solutionVersionId !== tableStore.solutionVersionId) return;
      // PSTUDIO-4168: a new change is new information — give it a full budget
      // even if the previous one exhausted its own.
      consecutiveRetries = 0;
      dirtyForSolutionVersionId = msg.solutionVersionId;
      maybeReload();
    });

    const onVisibilityOrFocus = () => maybeReload();
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
      if (watchdogId !== null) {
        clearTimeout(watchdogId);
        watchdogId = null;
      }
      clearRetry();
    };
  }, []);
}
